import no from './no.json';

export type TranslationFile = {
  [key: string]: string | TranslationFile;
};

type GetKey<
  A extends string | undefined,
  B extends string,
  C = A extends undefined ? B : `${A}.${B}`,
> = C;

type GetKeys<
  CurrentFile extends TranslationFile,
  CurrentKey extends string | undefined = undefined,
> = {
  [key in keyof CurrentFile]: key extends string
    ? CurrentFile[key] extends TranslationFile
      ? GetKeys<CurrentFile[key], GetKey<CurrentKey, key>>
      : GetKey<CurrentKey, key>
    : never;
}[keyof CurrentFile];

type TranslationFunc<Library extends TranslationFile> = (
  key: GetKeys<Library>,
  options?: { values: Record<string, string | number | boolean> }
) => string;

export const createI18n =
  <Library extends TranslationFile>(
    library: Library
  ): TranslationFunc<Library> =>
  (key, { values } = { values: {} }) => {
    let translation: TranslationFile | string = library;
    const keys = (key as string).split('.');
    keys.forEach((subKey) => {
      if (typeof translation === 'string') {
        return;
      }
      translation = translation[subKey];
    });
    if (!translation || typeof translation !== 'string') {
      console.error(`Invalid translation key: ${key}`);
    } else {
      // Split on {}, replace values and set possible plural values
      translation = (translation as string)
        .split('{')
        .join('}')
        .split('}')
        .map((subString, index) => {
          if (index % 2 === 1) {
            if (subString.startsWith('plural')) {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const [_indicator, replaceKey, singular, plural] =
                subString.split(', ');
              if (!replaceKey || !singular || !plural) {
                console.error(`Invalid plural translation: ${subString}`);
                return '';
              }
              return values[replaceKey] === 1 ? singular : plural;
            }
            if (subString.startsWith('or')) {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const [_indicator, replaceKey, either, or] =
                subString.split(', ');
              if (!replaceKey || !either || !or) {
                console.error(`Invalid plural translation: ${subString}`);
                return '';
              }
              return values[replaceKey] ? either : or;
            } else {
              return `${values[subString]}` ?? '';
            }
          }
          return subString;
        })
        .join('');
    }

    return translation as string;
  };

export const i18n = createI18n(no);
